import {
  CheckCircle2,
  Factory,
  Package,
  Sparkles,
  type LucideIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { UsinageStatus } from "@/features/milling/api/types"
import { cn } from "@/lib/utils"

// The user-facing three-step process. Backend statuses fold into these
// buckets:
//
//   SCHEDULED, RUNNING, PENDING_COUPLING → "plan"
//   COUPLAGE_PLANNED, FABRICATION       → "fabricate"
//   COMPLETED                           → "complete"
//
// PENDING_COUPLING is a transitional / legacy status that visually
// belongs to the planning step (operator hasn't committed a plan yet).
export type StepKey = "plan" | "fabricate" | "complete"

interface Step {
  key: StepKey
  label: string
  Icon: LucideIcon
}

function statusToStepIndex(status: UsinageStatus): number {
  switch (status) {
    case "COMPLETED":
      return 2
    case "COUPLAGE_PLANNED":
    case "FABRICATION":
      return 1
    case "SCHEDULED":
    case "RUNNING":
    case "PENDING_COUPLING":
    default:
      return 0
  }
}

interface UsinageStepperProps {
  status: UsinageStatus
  // Optional override that determines which tile reads as "current".
  // Used by CompletedView to let the operator browse past steps in
  // read-only mode while the underlying status is COMPLETED. When
  // omitted, the visual "current" is derived from `status`.
  activeStep?: StepKey
  // Optional click handler. When provided, past + current tiles
  // become buttons; clicking them invokes the handler with the step
  // key. Future tiles stay non-interactive. When omitted, the
  // stepper is a pure indicator.
  onStepChange?: (step: StepKey) => void
}

// Mirrors `LoadStepper` in intake / `Stepper` in transit for visual
// consistency across the exporter portal. Pure indicator unless an
// `onStepChange` handler is supplied — in which case past + current
// tiles act as buttons (used by CompletedView to surface read-only
// views of plan / fabricate / completed without changing the
// underlying status).
export function UsinageStepper({
  status,
  activeStep,
  onStepChange,
}: UsinageStepperProps) {
  const { t } = useTranslation("milling")
  const reachedIdx = statusToStepIndex(status)

  const steps: Step[] = [
    { key: "plan", label: t("stepper.plan"), Icon: Sparkles },
    { key: "fabricate", label: t("stepper.fabricate"), Icon: Package },
    { key: "complete", label: t("stepper.complete"), Icon: Factory },
  ]
  const activeIdx =
    activeStep != null
      ? steps.findIndex((s) => s.key === activeStep)
      : reachedIdx

  return (
    <Card className="p-3">
      <ol className="flex flex-wrap items-stretch gap-2">
        {steps.map((step, idx) => {
          // "Reached" means the run has progressed past this step.
          // "Active" is which tile is highlighted right now (may
          // differ from reached when the operator is browsing past
          // steps on a COMPLETED run).
          const isReached = idx < reachedIdx
          const isActive = idx === activeIdx
          const isFuture = idx > reachedIdx
          const navigable = onStepChange != null && !isFuture

          // Tile background: highlight active; neutral for done; muted
          // for unreached.
          const tile = isActive
            ? "border-primary/60 bg-primary/5 text-foreground"
            : isReached
              ? "border-border bg-card text-foreground"
              : "border-border bg-muted/40 text-muted-foreground"

          // Inner ring tone — primary for active, emerald for done,
          // muted otherwise.
          const ring = isActive
            ? "bg-primary text-primary-foreground"
            : isReached
              ? "bg-emerald-600 text-white"
              : "bg-muted text-muted-foreground"

          const tileClass = cn(
            "relative flex h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left",
            tile,
            navigable
              ? "cursor-pointer transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              : null,
          )
          const inner = (
            <>
              <span
                className={cn(
                  "flex size-6 flex-shrink-0 items-center justify-center rounded-full",
                  ring,
                )}
              >
                {isReached && !isActive ? (
                  <CheckCircle2 className="size-3.5" />
                ) : (
                  <step.Icon className="size-3.5" />
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col justify-center leading-tight">
                <div className="truncate text-xs font-medium">
                  {step.label}
                </div>
                {isActive ? (
                  <div className="text-[9px] font-semibold tracking-wider text-primary uppercase">
                    {t("stepper.currentBadge")}
                  </div>
                ) : null}
              </div>
            </>
          )

          return (
            <li key={step.key} className="min-w-0 flex-1 basis-32">
              <Tooltip>
                <TooltipTrigger asChild>
                  {navigable ? (
                    <button
                      type="button"
                      onClick={() => onStepChange(step.key)}
                      className={tileClass}
                      aria-current={isActive ? "step" : undefined}
                    >
                      {inner}
                    </button>
                  ) : (
                    <div
                      className={tileClass}
                      aria-current={isActive ? "step" : undefined}
                    >
                      {inner}
                    </div>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {isActive
                    ? t("stepper.activeHint")
                    : isReached
                      ? navigable
                        ? t("stepper.reviewHint")
                        : t("stepper.doneHint")
                      : t("stepper.lockedHint")}
                </TooltipContent>
              </Tooltip>
            </li>
          )
        })}
      </ol>
    </Card>
  )
}
